import React from 'react';
import _ from 'lodash';

import { Form, Grid, Tooltip } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';
import {
  CoordonneesContacts,
  TypeComponent,
} from '@mairistem/administres-utils';

import { Section, SectionHeader } from '@/components/Common';
import { COLORS } from '@/constants';
import { useTypesVoies } from '@/hooks';
import {
  estTypeAdresseCommune,
  findTypeVoie,
  formatAdresse,
  paysTypeAdresse,
  stripTypeVoie,
  toPhoneInputValue,
} from '@/utils';

interface InformationsContactProps {
  numeroPartenaire: number;
  tel1?: string;
  tel2?: string;
  mail?: string;
  adresseResidence?: any;
  residenceCommuneDelegue?: boolean;
  residenceLibelleCommuneDelegue?: string;
  errors?: Record<string, string>;
  onChange: (
    _e: any,
    data: { name: string; value?: any; checked?: boolean },
  ) => void;
}

const InformationsContact = (props: InformationsContactProps) => {
  const {
    numeroPartenaire,
    tel1,
    tel2,
    mail,
    adresseResidence,
    residenceCommuneDelegue,
    residenceLibelleCommuneDelegue,
    errors,
    onChange,
  } = props;

  const testIdPrefix = `assistant-partenaire-${numeroPartenaire}-contact`;

  // Pas de colonne type de voie pour la résidence côté API : à défaut d'un
  // type sélectionné dans le référentiel, il est déduit du libellé pour
  // l'étiquette du champ Voie, comme pour les adresses de la convention
  // (réintégré à l'envoi, cf. composeLibellesVoieConvention).
  // Seulement en « Commune » (cf. estTypeAdresseCommune).
  const typesVoies = useTypesVoies();
  const typeVoie = React.useMemo(
    () =>
      estTypeAdresseCommune(adresseResidence?.type)
        ? adresseResidence?.typeVoie ||
          findTypeVoie(adresseResidence?.libelleVoie, typesVoies)
        : null,
    [
      adresseResidence?.type,
      adresseResidence?.typeVoie,
      adresseResidence?.libelleVoie,
      typesVoies,
    ],
  );

  const handleResidenceFieldChange = React.useCallback(
    (field: string, value: string | boolean) => {
      onChange(null, {
        name: field,
        value,
      });
    },
    [onChange],
  );

  const handleAdresseItemChange = React.useCallback(
    ({ adresse }) => {
      // AdresseForm émet parfois une adresse vide (mises à jour groupées) :
      // l'appliquer remettrait l'adresse d'origine par-dessus la saisie.
      if (_.isEmpty(adresse)) return;

      onChange(null, {
        name: 'adresseResidence',
        value: formatAdresse(adresseResidence, { ...adresse }),
      });
    },
    [adresseResidence, onChange],
  );

  const handleAdresseChange = React.useCallback(
    ({ target: { name, value } }) => {
      // Seul le type est écrit : remplacer l'adresse par `{ type }` effaçait
      // celle que `onItemChange` vient de réinitialiser (commune déléguée…).
      if (name === 'adresse.type') {
        onChange(null, {
          name: 'adresseResidence.type',
          value,
        });
        // La réinitialisation d'AdresseForm conserve le type de voie de
        // l'adresse précédente.
        onChange(null, { name: 'adresseResidence.typeVoie', value: '' });
        const pays = paysTypeAdresse(value);
        if (pays) {
          onChange(null, { name: 'adresseResidence.libellePays', value: pays });
        }

        return;
      }

      const field = _.replace(name, 'adresse.', '');

      onChange(null, {
        name: 'adresseResidence',
        value: formatAdresse(adresseResidence, {
          [field]: value,
        }),
      });
    },
    [adresseResidence, onChange],
  );

  return (
    <Section data-testid={testIdPrefix} title="INFORMATIONS DE CONTACT">
      <div>
        {/* @ts-ignore */}
        <Form style={{ display: 'flex' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '1em',
              width: '50%',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 0 14px 0',
              }}
            >
              <SectionHeader title="Adresse de résidence" />
            </div>
            <div style={{ paddingLeft: '1em' }}>
              <AdresseForm
                saisie="libre"
                handleTypes
                displayDepartement={false}
                displayArticle
                adresse={{
                  type: adresseResidence?.type || 'Commune',
                  codePostal: adresseResidence?.libelleCodePostal,
                  commune: adresseResidence?.libelleCommune,
                  voie: stripTypeVoie(adresseResidence?.libelleVoie, typeVoie),
                  pays: adresseResidence?.libellePays,
                  numeroVoirie: adresseResidence?.numeroVoirie,
                  complementNumeroVoirie:
                    adresseResidence?.complementNumeroVoirie,
                  complementGeographique:
                    adresseResidence?.complementGeographique,
                  complementDistribution:
                    adresseResidence?.complementDistribution,
                  complementIdentification:
                    adresseResidence?.complementIdentification,
                  indiceRepetition: adresseResidence?.indiceRepetition,
                  typeVoie,
                  article: adresseResidence?.article,
                }}
                onItemChange={handleAdresseItemChange}
                onChange={handleAdresseChange}
              />
              <Grid>
                <Grid.Row verticalAlign="middle">
                  <Grid.Column width={6}>
                    <label>&nbsp;</label>
                    <Form.Checkbox
                      label="Commune déléguée"
                      data-testid={`${testIdPrefix}-commune-delegue`}
                      checked={residenceCommuneDelegue}
                      onChange={(_e, data) => {
                        handleResidenceFieldChange(
                          'adresseResidence.residenceCommuneDelegue',
                          data?.checked,
                        );

                        if (!data?.checked) {
                          handleResidenceFieldChange(
                            'adresseResidence.residenceLibelleCommuneDelegue',
                            '',
                          );
                        }
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column width={10}>
                    <Tooltip
                      trigger={
                        <div>
                          <Form.Input
                            label="&nbsp;"
                            input={{
                              'data-testid': `${testIdPrefix}-libelle-commune-delegue`,
                            }}
                            value={residenceLibelleCommuneDelegue ?? ''}
                            disabled={!residenceCommuneDelegue}
                            error={!!errors?.residenceLibelleCommuneDelegue}
                            onChange={(_e: any, data: any) =>
                              handleResidenceFieldChange(
                                'adresseResidence.residenceLibelleCommuneDelegue',
                                data?.value ?? '',
                              )
                            }
                          />
                        </div>
                      }
                      type={
                        errors?.residenceLibelleCommuneDelegue ? 'error' : ''
                      }
                      message={errors?.residenceLibelleCommuneDelegue}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </div>
          </div>
          <div
            style={{
              border: `1px solid ${COLORS.DividerColor}`,
              margin: '0px 2rem',
              width: 1,
            }}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: 14,
            }}
          >
            <SectionHeader title="Contacts" />
            <div style={{ paddingLeft: '1em' }}>
              <CoordonneesContacts
                typeComponent={TypeComponent.PACS}
                tel1={toPhoneInputValue(tel1) ?? ''}
                tel2={toPhoneInputValue(tel2) ?? ''}
                mail={mail}
                onChange={onChange}
              />
            </div>
          </div>
        </Form>
      </div>
    </Section>
  );
};

export default InformationsContact;
